import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { DynamicForm } from 'dynamic-form/interfaces/dynamic-form-elements.interface';
import { ListingsService } from 'listings/services/listings.service';
import { map, Observable, of, switchMap } from 'rxjs';
import { ContextAwareSidebarService } from 'shared/services/context-aware-sidebar.service';

const ROOM_TYPE_FEATURE_GROUP_FIELD_NAME = 'roomType';

const LISTING_API = environment.BASE_URL + 'listingApi/';

export interface RoomCreationPayload {
    roomCode: string;
    roomType: number;
    roomName: string;
}

export interface RoomCreationResponse {
    id: string;
}

@Injectable({
    providedIn: 'root',
})
export class RoomsFeatureGroupsService {
    currentlyLoadedRoomFeatureGroupPath = signal<string | null | undefined>(null);

    currentlyLoadedRoomFeatureGroupId = computed(() => {
        const navigationItems = this.contextAwareSidebarService.sidebarData();
        const flattenedNavigationItems = navigationItems.flatMap((navigationItem) => [navigationItem, ...(navigationItem.children ?? [])]);
        const currentRoomFeatureGroupPath = this.currentlyLoadedRoomFeatureGroupPath();
        return flattenedNavigationItems.find((navigationItem) => navigationItem.fieldName === currentRoomFeatureGroupPath)?.id ?? null;
    });
    currentlyLoadedRoomFeatureGroupId$ = toObservable(this.currentlyLoadedRoomFeatureGroupId);

    currentRoomFeatureGroupData = toSignal(
        this.currentlyLoadedRoomFeatureGroupId$.pipe(
            switchMap((featureGroupId) => {
                if (!featureGroupId) {
                    return of(null);
                }
                return this.getNavigationFeatureDetails(featureGroupId);
            }),
        ),
    );

    currentRoomFeatureGroupFieldIds = computed(() => {
        const currentRoomFeatureGroupData = this.currentRoomFeatureGroupData();
        return currentRoomFeatureGroupData?.featureDetailIds ?? [];
    });

    currentRoomFeatureGroupFieldIds$ = toObservable(this.currentRoomFeatureGroupFieldIds);

    constructor(
        private listingsService: ListingsService,
        private contextAwareSidebarService: ContextAwareSidebarService,
        private http: HttpClient,
    ) {}

    public getNavigationFeatureDetails(navigationItemId: string): Observable<{ featureDetailIds: string[] }> {
        return this.http.get<{ featureDetailIds: string[] }>(
            `${LISTING_API}room-overview/navigation-feature-details?navigationItemId=${navigationItemId}`,
        );
    }

    public getRoomTypeFeatureGroupId(listingId: string): Observable<string | null> {
        return this.listingsService
            .getFeatureGroups(listingId)
            .pipe(
                map(
                    (featureGroups) =>
                        featureGroups.find((featureGroup) => featureGroup.fieldName === ROOM_TYPE_FEATURE_GROUP_FIELD_NAME)?.id ?? null,
                ),
            );
    }

    public getFormStructure(listingId: string, featureGroupId: string): Observable<DynamicForm> {
        return this.listingsService.getFormStructure(listingId, featureGroupId);
    }

    public getFormValues(listingId: string, featureGroupId: string): Observable<any> {
        return this.listingsService.getFormValues(listingId, featureGroupId);
    }

    public createRoom(listingId: string, roomData: RoomCreationPayload): Observable<RoomCreationResponse> {
        return this.http.post<RoomCreationResponse>(`${LISTING_API}room-overview/create`, roomData, {
            params: { listingId },
        });
    }

    public updateValues(listingId: string, roomId: string, changedData: any): Observable<any> {
        return this.http.patch(`${LISTING_API}room-overview/room-type-partial-update`, changedData, {
            params: { listingId, roomId },
        });
    }

    public deleteRoom(listingId: string, roomId: string): Observable<any> {
        return this.http.delete(`${LISTING_API}room-overview/delete`, {
            params: { listingId, roomId },
        });
    }
}
